import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { Reveal, Overline } from "@/components/Reveal";
import { FinalCta } from "@/components/sections/CallToActions";
import { useLang } from "@/context/LanguageContext";
import NotFoundPage from "@/pages/NotFoundPage";

const LEGACY_REDIRECTS = {
  "acompanamiento-personal": "servicios-estados-unidos",
  "negocios-emprendimiento": "servicios-estados-unidos",
};

export default function ElSalvadorUsaSectionPage() {
  const { lang, t } = useLang();
  const { sectionId } = useParams();
  if (LEGACY_REDIRECTS[sectionId]) {
    return <Navigate to={`/${lang}/el-salvador-ee-uu/${LEGACY_REDIRECTS[sectionId]}`} replace />;
  }
  const s = t.diasporaPage.sections.find((x) => x.id === sectionId);
  if (!s) return <NotFoundPage />;
  return (
    <>
      <Seo page="diaspora" title={`${s.title} | ${t.brandName}`} description={s.summary} />
      <section className="py-14 md:py-20" data-testid="diaspora-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="mb-10 md:mb-12">
            <img
              src="/assets/diaspora-header.webp"
              alt={t.diasporaPage.imageAlt}
              data-testid="diaspora-section-header-image"
              className="w-full h-44 md:h-64 object-cover"
            />
          </Reveal>
          <Reveal>
            <Link
              to={`/${lang}/el-salvador-ee-uu`}
              data-testid="diaspora-back-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-navy/60 hover:text-ser-purple transition-colors"
            >
              <ArrowLeft size={16} strokeWidth={1.5} className="transition-transform group-hover:-translate-x-1" />
              {t.diasporaPage.backTo}
            </Link>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal delay={0.05}>
                <Overline>{s.kicker}</Overline>
                <h1 className="font-display text-4xl md:text-5xl leading-[1.08] tracking-tight text-navy mt-4">
                  {s.title}
                </h1>
                <blockquote className="mt-8 border-l-2 border-ser-yellow pl-6">
                  <p className="font-display italic text-xl md:text-2xl leading-snug text-navy/85">{s.lead}</p>
                </blockquote>
                <div className="mt-8 space-y-5 text-base leading-relaxed text-navy/75" data-testid="diaspora-section-paragraphs">
                  {s.paragraphs.map((p, i) => {
                    const ml = s.memberLink;
                    if (ml && p.includes(ml.name)) {
                      const [before, after] = p.split(ml.name);
                      return (
                        <p key={i}>
                          {before}
                          <Link
                            to={`/${lang}/team/${ml.id}`}
                            data-testid="diaspora-member-link"
                            className="font-medium text-ser-purple underline decoration-ser-yellow decoration-2 underline-offset-4 hover:text-navy transition-colors"
                          >
                            {ml.name}
                          </Link>
                          {after}
                        </p>
                      );
                    }
                    return <p key={i}>{p}</p>;
                  })}
                </div>
              </Reveal>
              {s.subBlock && (
                <Reveal delay={0.15}>
                  <div className="mt-12">
                    <h2 className="font-display text-2xl text-navy">{s.subBlock.title}</h2>
                    <p className="mt-4 text-base leading-relaxed text-navy/75">{s.subBlock.text}</p>
                    <p className="mt-6 border-l-4 border-ser-yellow pl-5 font-display text-xl md:text-2xl leading-snug text-navy">
                      {s.subBlock.phrase}
                    </p>
                  </div>
                </Reveal>
              )}
              {s.helpBlock && (
                <Reveal delay={0.2}>
                  <div className="mt-12">
                    <h2 className="font-display text-2xl text-navy">{s.helpBlock.title}</h2>
                    <p className="mt-4 text-base leading-relaxed text-navy/75">{s.helpBlock.text}</p>
                  </div>
                </Reveal>
              )}
              {s.noteBlock && (
                <Reveal delay={0.15}>
                  <div className="mt-12">
                    <h2 className="font-display text-2xl text-navy">{s.noteBlock.title}</h2>
                    <p className="mt-4 text-base leading-relaxed text-navy/75">{s.noteBlock.text}</p>
                    <p className="mt-6 border-l-4 border-ser-yellow pl-5 font-display text-xl md:text-2xl leading-snug text-navy">
                      {s.noteBlock.closing}
                    </p>
                  </div>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="border border-navy/10 bg-white p-8 md:p-10">
                  <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-ser-purple">{s.listTitle}</h2>
                  <ul className="mt-6 space-y-3.5" data-testid="diaspora-section-list">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm md:text-base text-navy/80">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ser-blue shrink-0" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  {s.note && <p className="mt-6 text-sm leading-relaxed text-navy/60 italic">{s.note}</p>}
                  {s.items2 && (
                    <>
                      <h2 className="mt-10 text-xs uppercase tracking-[0.2em] font-medium text-ser-purple">{s.list2Title}</h2>
                      <ul className="mt-6 space-y-3.5" data-testid="diaspora-section-list-2">
                        {s.items2.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-sm md:text-base text-navy/80">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ser-blue shrink-0" aria-hidden="true" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <Link
                    to={`/${lang}/contact`}
                    data-testid="diaspora-section-cta"
                    className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-ser-purple hover:text-navy transition-colors"
                  >
                    {s.cta}
                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
          {s.values && (
            <Reveal className="mt-16 border-t border-navy/5 pt-14" >
              <div data-testid="diaspora-section-values">
                <h2 className="text-xs uppercase tracking-[0.2em] text-navy/50 font-medium">{s.values.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-navy/70 max-w-2xl">{s.values.intro}</p>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  {s.values.items.map((v) => (
                    <div key={v.name} className="bg-white border border-navy/5 p-6">
                      <p className="font-display text-lg text-ser-purple">{v.name}</p>
                      <span className="block w-8 h-0.5 bg-ser-yellow mt-3" aria-hidden="true" />
                      <p className="mt-3 text-sm leading-relaxed text-navy/70">{v.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-8 font-display text-xl md:text-2xl leading-snug text-navy max-w-3xl">{s.values.closing}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
